<template>
    <section class="slice acquisition-barometer">
        <div class="slice-content">
            <div v-if="barometer" class="info__panel" :class="{'no-explanation': !jobExplanation}">
                <div class="barometer">
                    <!-- eslint-disable vue/no-v-html -->
                    <h3 class="info__title" data-testid="acquisition-barometer-title" v-html="barometerTitle" />
                    <p v-html="barometerText" />
                    <p v-html="barometerTextExtra" />
                    <!-- eslint-enable vue/no-v-html -->
                </div>
            </div>
            <div v-if="jobExplanation" class="info__panel">
                <div class="job-explanation">
                    <PrismicRichText v-if="prismicPage?.data?.jobExplanationTitle" :field="prismicPage?.data?.jobExplanationTitle" />
                    <h3 v-else class="info__title job-explanation__title">{{ jobExplanationTitle }}</h3>
                    <PrismicRichText v-if="prismicPage?.data?.jobExplanationDescription" :field="prismicPage?.data?.jobExplanationDescription" />
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-else-if="jobExplanation"
                        data-testid="acquisition-job-explanation"
                        class="job-explanation__content"
                        v-html="jobExplanation"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {ref, computed, inject} from 'vue';
    import {PrismicRichText} from '@prismicio/vue';
    import {useTranslation} from '#imports';
    import {JOB_PAGE} from '~/keys';

    const {t} = useTranslation();
    const {landingPage, prismicPage} = inject(JOB_PAGE) || {};
    const {location, barometer} = landingPage || {};

    // helper
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    // view
    const jobLabel = landingPage?.textualContent.thisPage.jobLabelSingular;
    const barometerTitle = t('landing.acquisition.barometer.title', [jobLabel]);

    const barometerText = computed(() => {
        if (location) {
            const averagePrice = barometer?.averagePricesPerTown?.find((price) => price.city === location.id);
            return t('landing.acquisition.barometer.average.price.localized', [averagePrice?.price, location.label]);
        }
        return t('landing.acquisition.barometer.average.price', [landingPage?.barometer?.averagePrice]);
    });

    const barometerTextExtra = computed(() => {
        const averagePricesPerTown = landingPage?.barometer?.averagePricesPerTown || [];
        if (averagePricesPerTown.length) {
            return t('acquisition.stats.comparison.price', [
                jobLabel,
                capitalize(averagePricesPerTown[0].city || ''),
                averagePricesPerTown[0].price,
                capitalize(averagePricesPerTown[1].city || ''),
                averagePricesPerTown[1].price,
                averagePricesPerTown[2].price,
                capitalize(averagePricesPerTown[2].city || ''),
            ]);
        }
        return '';
    });

    const jobExplanationTitle = t('landing.acquisition.job.explanation.title', [jobLabel]);
    const jobExplanation = ref(landingPage?.textualContent.reassurance.value?.replaceAll('<a ', '<a class="link" '));
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .acquisition-barometer {
        .slice-content {
            text-align: left;
            display: flex;
            flex-direction: column;

            > div {
                margin: 0 0 var(--joy-core-spacing-8);
            }

            @include mq.screen_md(min) {
                flex-direction: row;
                align-items: stretch;
                justify-content: center;

                > div {
                    flex: none;
                    width: 50%;
                    margin: 0 var(--joy-core-spacing-4);
                }
            }
        }

        .info {
            &__panel {
                border-radius: var(--joy-core-radius-3);
            }

            &__title {
                font-family: var(--joy-font-family-title);
                font-weight: var(--joy-font-weight-bold);
                line-height: var(--joy-line-height-small);
                margin-bottom: var(--joy-core-spacing-8);
                font-size: var(--joy-font-size-secondary-200);

                @include mq.screen_md(min) {
                    font-size: var(--joy-font-size-secondary-400);
                }
            }
        }

        .barometer {
            padding: var(--joy-core-spacing-8);
            border-radius: var(--joy-core-radius-3);
            background: url('https://dam.malt.com/acquisition/barometer/barometer') var(--joy-color-primary-50) bottom/cover;
            padding-bottom: 180px;
            color: var(--joy-color-neutral-0);

            p {
                margin-bottom: var(--joy-core-spacing-4);
                max-width: 350px;
            }

            &.no-explanation {
                width: 70%;
            }
        }

        .job-explanation {
            padding: var(--joy-core-spacing-8);
            border-radius: var(--joy-core-radius-3);
            background: var(--joy-color-background-default);
            box-shadow: 0 var(--joy-core-spacing-1) var(--joy-core-spacing-5) 0 rgb(51 51 48 / 20%);
            min-height: 100%;
            text-align: left;
            max-width: none !important;

            &__title {
                color: var(--joy-color-text-default);
            }
        }
    }
</style>
